import { useState } from "react";
import CopyCode from "../CopyCode/CopyCode";
import Icon from "../Icon";

const ListComponent = () => {
	const [checkedItems, setCheckedItems] = useState({});

	const toggleCheck = (id) => {
		setCheckedItems(prev => ({
			...prev,
			[id]: !prev[id]
		}));
	};

	return (
		<div className="flex flex-col gap-y-10 p-6">
			{/* SECTION 1: BASIC LISTS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="fileText" className="w-5 h-5" /> List Types</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 1: Unordered List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Unordered List</label>
						<ul className="list-disc list-inside text-slate-300 space-y-2">
							<li>First item in the list</li>
							<li>Second item in the list</li>
							<li>Third item in the list</li>
							<li>Fourth item in the list</li>
						</ul>
						<CopyCode number_code={1} code={`<ul className="list-disc list-inside text-slate-300 space-y-2">
  <li>First item</li>
  <li>Second item</li>
  <li>Third item</li>
</ul>`} />
					</div>

					{/* Variation 2: Ordered List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Ordered List</label>
						<ol className="list-decimal list-inside text-slate-300 space-y-2">
							<li>Step one</li>
							<li>Step two</li>
							<li>Step three</li>
							<li>Final step</li>
						</ol>
						<CopyCode number_code={2} code={`<ol className="list-decimal list-inside text-slate-300 space-y-2">
  <li>Step one</li>
  <li>Step two</li>
  <li>Step three</li>
</ol>`} />
					</div>

					{/* Variation 3: Alphabet List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Letter List</label>
						<ol className="list-inside text-slate-300 space-y-2" style={{listStyleType: 'lower-alpha'}}>
							<li>First alphabetical item</li>
							<li>Second alphabetical item</li>
							<li>Third alphabetical item</li>
						</ol>
						<CopyCode number_code={3} code={`<ol style={{listStyleType: 'lower-alpha'}} className="list-inside text-slate-300 space-y-2">
  <li>Item A</li>
  <li>Item B</li>
  <li>Item C</li>
</ol>`} />
					</div>

					{/* Variation 4: No Bullets */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">No Style List</label>
						<ul className="list-none text-slate-300 space-y-2">
							<li>Plain item one</li>
							<li>Plain item two</li>
							<li>Plain item three</li>
							<li>Plain item four</li>
						</ul>
						<CopyCode number_code={4} code={`<ul className="list-none text-slate-300 space-y-2">
  <li>Plain item</li>
  <li>Plain item</li>
</ul>`} />
					</div>

					{/* Variation 5: Custom Bullets */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Custom Icon Bullets</label>
						<ul className="list-none text-slate-300 space-y-2 pl-0">
							<li className="flex items-start gap-3"><span className="flex-shrink-0"><Icon name="target" className="w-4 h-4" /></span><span>Goal item one</span></li>
							<li className="flex items-start gap-3"><span className="flex-shrink-0"><Icon name="check" className="w-4 h-4" /></span><span>Completed item</span></li>
							<li className="flex items-start gap-3"><span className="flex-shrink-0"><Icon name="pin" className="w-4 h-4" /></span><span>Important note</span></li>
							<li className="flex items-start gap-3"><span className="flex-shrink-0"><Icon name="lightbulb" className="w-4 h-4" /></span><span>Idea item</span></li>
						</ul>
						<CopyCode number_code={5} code={`<ul className="list-none text-slate-300 space-y-2">
  <li className="flex items-start gap-3">
	<span>Icon</span><span>Goal item</span>
  </li>
  <li className="flex items-start gap-3">
	<span>Icon</span><span>Completed</span>
  </li>
</ul>`} />
					</div>

					{/* Variation 6: Column List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Multi-Column List</label>
						<ul className="list-disc list-inside text-slate-300 space-y-1 columns-2">
							<li>Column item one</li>
							<li>Column item two</li>
							<li>Column item three</li>
							<li>Column item four</li>
							<li>Column item five</li>
							<li>Column item six</li>
						</ul>
						<CopyCode number_code={6} code={`<ul className="list-disc list-inside text-slate-300 space-y-1 columns-2">
  <li>Item</li>
  <li>Item</li>
  <li>Item</li>
  <!-- More items -->
</ul>`} />
					</div>

				</div>
			</div>

			{/* SECTION 2: ADVANCED LISTS */}
			<div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
				<h2 className="text-xl font-bold text-amber-400 mb-6 flex items-center gap-2"><Icon name="sparkles" className="w-5 h-5" /> Advanced Lists</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">

					{/* Variation 7: Checklist */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Checklist</label>
						<div className="space-y-2">
							{['Buy groceries', 'Write report', 'Call client', 'Review code'].map((item, i) => (
								<label key={i} className="flex items-center gap-3 cursor-pointer text-slate-300 hover:text-slate-100 transition-colors">
									<input 
										type="checkbox" 
										checked={checkedItems[`check${i}`] || false}
										onChange={() => toggleCheck(`check${i}`)}
										className="w-4 h-4"
									/>
									<span className={checkedItems[`check${i}`] ? 'line-through text-slate-500' : ''}>{item}</span>
								</label>
							))}
						</div>
						<CopyCode number_code={7} code={`<label className="flex items-center gap-3 cursor-pointer">
  <input type="checkbox" className="w-4 h-4" />
  <span>Task item</span>
</label>`} />
					</div>

					{/* Variation 8: Icon List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Icon List</label>
						<ul className="space-y-2 text-slate-300">
							<li className="flex items-center gap-3"><span className="text-lg"><Icon name="check" className="w-4 h-4" /></span> <span className="text-green-400">Approved</span></li>
							<li className="flex items-center gap-3"><span className="text-lg"><Icon name="clock" className="w-4 h-4" /></span> <span className="text-amber-400">Pending</span></li>
							<li className="flex items-center gap-3"><span className="text-lg"><Icon name="close" className="w-4 h-4" /></span> <span className="text-red-400">Rejected</span></li>
							<li className="flex items-center gap-3"><span className="text-lg"><Icon name="arrowRight" className="w-4 h-4" /></span> <span className="text-blue-400">In Review</span></li>
						</ul>
						<CopyCode number_code={8} code={`<ul className="space-y-2 text-slate-300">
  <li className="flex items-center gap-3">
	<span>Icon</span> <span className="text-green-400">Approved</span>
  </li>
</ul>`} />
					</div>

					{/* Variation 9: Nested List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Nested List</label>
						<ul className="list-disc list-inside text-slate-300 space-y-1">
							<li>Parent item one
								<ul className="list-disc list-inside pl-4 text-slate-400 space-y-1">
									<li>Child item</li>
									<li>Child item</li>
								</ul>
							</li>
							<li>Parent item two</li>
						</ul>
						<CopyCode number_code={9} code={`<ul className="list-disc list-inside text-slate-300 space-y-1">
  <li>Parent
    <ul className="list-disc list-inside pl-4 text-slate-400">
      <li>Child</li>
      <li>Child</li>
    </ul>
  </li>
</ul>`} />
					</div>

					{/* Variation 10: Definition List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Definition List</label>
						<dl className="space-y-3">
							<div>
								<dt className="font-semibold text-amber-400">Term One</dt>
								<dd className="text-slate-400 text-sm pl-4">Definition of the term</dd>
							</div>
							<div>
								<dt className="font-semibold text-amber-400">Term Two</dt>
								<dd className="text-slate-400 text-sm pl-4">Explanation of the term</dd>
							</div>
						</dl>
						<CopyCode number_code={10} code={`<dl className="space-y-3">
  <div>
    <dt className="font-semibold text-amber-400">Term</dt>
    <dd className="text-slate-400 text-sm pl-4">Definition</dd>
  </div>
</dl>`} />
					</div>

					{/* Variation 11: Styled List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Styled List</label>
						<ul className="space-y-2">
							{['Feature One', 'Feature Two', 'Feature Three'].map((feature, i) => (
								<li key={i} className="p-2 bg-slate-800 border border-slate-700 rounded hover:border-amber-500/50 transition-all flex items-center gap-2 text-slate-300">
									<span className="text-amber-400"><Icon name="arrowRight" className="w-4 h-4" /></span> {feature}
								</li>
							))}
						</ul>
						<CopyCode number_code={11} code={`<ul className="space-y-2">
  {items.map(item => (
    <li key={item.id} className="p-2 bg-slate-800 border border-slate-700 rounded hover:border-amber-500/50">
	<span className="text-amber-400">Icon</span> {item.name}
    </li>
  ))}
</ul>`} />
					</div>

					{/* Variation 12: Expandable List */}
					<div className="p-3 bg-slate-900 border border-slate-600 rounded-lg overflow-hidden">
						<label className="text-xs text-slate-400 mb-3 block font-medium">Expandable List</label>
						<details className="space-y-2">
							<summary className="cursor-pointer text-amber-400 hover:text-amber-300 font-semibold">+ Expand to see items</summary>
							<ul className="list-disc list-inside text-slate-300 space-y-1 pl-4">
								<li>Hidden item one</li>
								<li>Hidden item two</li>
								<li>Hidden item three</li>
								<li>Hidden item four</li>
							</ul>
						</details>
						<CopyCode number_code={12} code={`<details className="space-y-2">
  <summary className="cursor-pointer text-amber-400 font-semibold">
    + Expand
  </summary>
  <ul className="list-disc list-inside text-slate-300 pl-4">
    <li>Hidden item</li>
  </ul>
</details>`} />
					</div>

				</div>
			</div>
		</div>
	);
};

export default ListComponent;


